import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import Spinner from './Spinner';
import TableService from '../services/table.service';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const errMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message;

const EMPTY = { name: '', note: '', active: true };

// Admin page (/admin/table): CRUD shop tables + copy the public ordering link.
const TableManagement = () => {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrTable, setQrTable] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  useEffect(() => {
    if (!qrTable) return;
    const onKey = (e) => e.key === 'Escape' && setQrTable(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [qrTable]);

  const load = () => {
    setLoading(true);
    TableService.getTables()
      .then((res) => setTables(res.data || []))
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    const req = editingId ? TableService.updateTable(editingId, form) : TableService.createTable(form);
    req
      .then(() => {
        toast.success(editingId ? t('tables.updated') : t('tables.created'));
        resetForm();
        load();
      })
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setSaving(false));
  };

  const edit = (tbl) => {
    setEditingId(tbl.id);
    setForm({ name: tbl.name, note: tbl.note || '', active: !!tbl.active });
  };

  const remove = (tbl) => {
    if (!window.confirm(t('tables.confirmDelete', { name: tbl.name }))) return;
    TableService.removeTable(tbl.id)
      .then(() => {
        toast.success(t('tables.deleted'));
        if (editingId === tbl.id) resetForm();
        load();
      })
      .catch((err) => toast.error(errMsg(err)));
  };

  // Public ordering link uses the (unique) table name, URL-encoded.
  // --- Drag & drop row reordering (native HTML5 DnD) ---
  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id)); // required by Firefox
  };

  const onDragOver = (e, id) => {
    if (dragId == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overId !== id) setOverId(id);
  };

  const onDragEnd = () => {
    setDragId(null);
    setOverId(null);
  };

  const onDrop = (e, targetId) => {
    e.preventDefault();
    const fromId = dragId;
    onDragEnd();
    if (fromId == null || fromId === targetId) return;
    const prev = tables;
    const next = [...tables];
    const from = next.findIndex((x) => x.id === fromId);
    const to = next.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setTables(next); // optimistic
    TableService.reorderTables(next.map((x) => x.id))
      .then((res) => {
        setTables(res.data || next);
        toast.success(t('tables.reordered'));
      })
      .catch((err) => {
        setTables(prev);
        toast.error(errMsg(err));
      });
  };

  const linkFor = (name) => `${window.location.origin}/order-by-table/${encodeURIComponent(name)}`;

  const qrDataUrl = () => {
    const canvas = document.getElementById('table-qr-canvas');
    return canvas ? canvas.toDataURL('image/png') : null;
  };

  const downloadQr = () => {
    const url = qrDataUrl();
    if (!url || !qrTable) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `table-${qrTable.name}-qr.png`;
    a.click();
  };

  const printQr = () => {
    const url = qrDataUrl();
    if (!url || !qrTable) return;
    const w = window.open('', '_blank', 'width=420,height=520');
    if (!w) return;
    const doc = w.document;
    doc.title = qrTable.name;
    const wrap = doc.createElement('div');
    wrap.style.cssText = 'text-align:center;font-family:sans-serif;padding:24px';
    const h = doc.createElement('h2');
    h.textContent = qrTable.name;
    const img = doc.createElement('img');
    img.style.width = '300px';
    img.onload = () => {
      w.focus();
      w.print();
    };
    img.src = url;
    wrap.appendChild(h);
    wrap.appendChild(img);
    doc.body.appendChild(wrap);
  };

  const copyLink = (name) => {
    const link = linkFor(name);
    const done = () => toast.success(t('tables.linkCopied'));
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(done, () => window.prompt(t('tables.copyManual'), link));
    } else {
      window.prompt(t('tables.copyManual'), link);
    }
  };

  return (
    <div className='container mt-4'>
      <h3 className='mb-1'>{t('tables.title')}</h3>
      <p className='text-muted'>{t('tables.subtitle')}</p>

      <form className='row g-2 align-items-end mb-4' onSubmit={submit}>
        <div className='col-md-3'>
          <label className='form-label small mb-1'>{t('tables.name')}</label>
          <input type='text' className='form-control' placeholder={t('tables.namePlaceholder')} value={form.name} maxLength={100} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className='col-md-4'>
          <label className='form-label small mb-1'>{t('tables.note')}</label>
          <input type='text' className='form-control' placeholder={t('tables.notePlaceholder')} value={form.note} maxLength={255} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <div className='col-md-2'>
          <div className='form-check mt-4'>
            <input className='form-check-input' type='checkbox' id='table-active' checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <label className='form-check-label' htmlFor='table-active'>
              {t('tables.active')}
            </label>
          </div>
        </div>
        <div className='col-md-3 d-flex gap-2'>
          <button className='btn btn-primary' type='submit' disabled={saving || !form.name.trim()}>
            {editingId ? t('common.update') : t('common.create')}
          </button>
          {editingId && (
            <button className='btn btn-outline-secondary' type='button' onClick={resetForm}>
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <Spinner />
      ) : tables.length === 0 ? (
        <p className='text-muted'>{t('tables.empty')}</p>
      ) : (
        <div className='table-responsive rounded'>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                <th>{t('tables.name')}</th>
                <th>{t('tables.note')}</th>
                <th style={{ width: 110 }}>{t('tables.status')}</th>
                <th>{t('tables.orderLink')}</th>
                <th style={{ width: 400 }}></th>
              </tr>
            </thead>
            <tbody>
              {tables.map((tbl) => (
                <tr key={tbl.id} onDragOver={(e) => onDragOver(e, tbl.id)} onDrop={(e) => onDrop(e, tbl.id)} className={overId === tbl.id && dragId !== tbl.id ? 'table-active' : undefined} style={{ opacity: dragId === tbl.id ? 0.4 : 1 }}>
                  <td draggable onDragStart={(e) => onDragStart(e, tbl.id)} onDragEnd={onDragEnd} title={t('tables.dragToReorder')} className='text-muted user-select-none' style={{ cursor: 'grab', fontSize: '1.2rem' }}>
                    ⠿
                  </td>
                  <td className='fw-semibold'>{tbl.name}</td>
                  <td className='small text-muted'>{tbl.note}</td>
                  <td>
                    <span className={`badge ${tbl.active ? 'bg-success' : 'bg-secondary'}`}>{tbl.active ? t('tables.active') : t('tables.inactive')}</span>
                  </td>
                  <td>
                    <div className='d-flex align-items-center gap-2'>
                      <a href={linkFor(tbl.name)} target='_blank' rel='noreferrer' className='small text-truncate' style={{ maxWidth: 220 }}>
                        /order-by-table/{tbl.name}
                      </a>
                    </div>
                  </td>
                  <td className='text-end'>
                    <Link className='btn btn-sm btn-outline-secondary me-2' to={`/order-by-table/${encodeURIComponent(tbl.name)}`}>
                      {t('tables.goTo')}
                    </Link>
                    <button className='btn btn-sm btn-outline-secondary me-2' type='button' onClick={() => copyLink(tbl.name)}>
                      {t('tables.copy')}
                    </button>
                    <button className='btn btn-sm btn-outline-success me-2' type='button' onClick={() => setQrTable(tbl)}>
                      {t('tables.qrCode')}
                    </button>
                    <button className='btn btn-sm btn-outline-primary me-2' onClick={() => edit(tbl)}>
                      {t('common.edit')}
                    </button>
                    <button className='btn btn-sm btn-outline-danger' onClick={() => remove(tbl)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {qrTable && (
        <>
          <div className='modal d-block' tabIndex='-1' role='dialog' onClick={() => setQrTable(null)}>
            <div className='modal-dialog modal-dialog-centered' role='document' onClick={(e) => e.stopPropagation()}>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>{t('tables.qrTitle', { name: qrTable.name })}</h5>
                  <button type='button' className='btn-close' aria-label={t('tables.close')} onClick={() => setQrTable(null)} />
                </div>
                <div className='modal-body text-center'>
                  <QRCodeCanvas id='table-qr-canvas' value={linkFor(qrTable.name)} size={256} level='M' marginSize={4} />
                  <div className='small text-muted mt-2 text-break'>{linkFor(qrTable.name)}</div>
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-outline-secondary' onClick={() => copyLink(qrTable.name)}>
                    {t('tables.copy')}
                  </button>
                  <button type='button' className='btn btn-outline-secondary' onClick={printQr}>
                    {t('tables.printQr')}
                  </button>
                  <button type='button' className='btn btn-primary' onClick={downloadQr}>
                    {t('tables.downloadQr')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop show' />
        </>
      )}
    </div>
  );
};

export default TableManagement;
